import React, { useState, useEffect, useContext } from "react";
import { Box, Typography, Button, Avatar, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import ServiceTrainer1 from "../../assets/showcases/trainer/service1.jpg";
import Service4 from "../../assets/showcases/salon/service3.jpeg";
import ServiceBarber4 from "../../assets/showcases/barber/service4.jpg";

import { CalendarOutline, TimeOutline, PersonOutline } from "react-ionicons";
import { CreatorContext } from "../../context/CreatorContext";
import chroma from "chroma-js";

const ShowcaseBookingConfirmation = ({
	sector,
	appointment,
	showAllIcons,
	showImage,
	showShadow,
	showBorderRadius,
	cardBackgroundColor,
}) => {
	const { t } = useTranslation();
	const [content, setContent] = useState(null);
	const { creator } = useContext(CreatorContext);

	const getTextColorBasedOnBackground = (bgColor) => {
		const r = parseInt(bgColor.substring(1, 3), 16);
		const g = parseInt(bgColor.substring(3, 5), 16);
		const b = parseInt(bgColor.substring(5, 7), 16);
		const brightness = (r * 299 + g * 587 + b * 114) / 1000;
		return brightness > 128 ? "#000000" : "#FFF";
	};

	const CustomTextField = ({ label, type, multiline }) => {
		const getDynamicColor = (background) => {
			if (creator.themeMode === "Dark" && creator.inputType === "input3") {
				return chroma(background).brighten(0.2).hex();
			} else if (
				creator.themeMode === "Light" &&
				creator.inputType === "input3"
			) {
				return chroma(background).darken(0.12).hex();
			} else if (creator.themeMode === "Dark") {
				return chroma(background).brighten(1).hex();
			} else {
				return chroma(background).darken(1).hex();
			}
		};

		const backgroundColor = getDynamicColor(creator.themeColor);
		const borderColor = getDynamicColor(creator.themeColor);
		const placeholderTextColor =
			creator.themeMode === "Dark"
				? chroma(creator.themeColor).brighten(1).hex()
				: chroma(creator.themeColor).darken(1).hex();

		return (
			<Box sx={{ marginBottom: "8px" }}>
				{/* Etiqueta del campo */}

				{type === "outline" && (
					<Box
						sx={{
							border: `1px solid ${borderColor}`,
							backgroundColor: creator.themeColor,
							borderRadius: "4px",
							height: multiline ? "200px" : "30px",
							display: "flex",
							alignItems: "center",
							padding: "0 12px",
						}}
					>
						<Typography
							sx={{
								fontSize: "8px",
								color: "#C0C0C0",
							}}
						>
							{label}
						</Typography>
					</Box>
				)}

				{type === "filled" && (
					<Box
						sx={{
							backgroundColor: backgroundColor,
							borderRadius: "4px",
							height: "30px",
							display: "flex",
							alignItems: "center",
							padding: "0 12px",
							border: "1px solid transparent",
						}}
					>
						<Typography
							sx={{
								fontSize: "8px",
								color: placeholderTextColor,
							}}
						>
							{label}
						</Typography>
					</Box>
				)}

				{type === "standard" && (
					<Box
						sx={{
							borderBottom: `1px solid ${borderColor}`,
							backgroundColor: "transparent",
							height: "30px",
							display: "flex",
							alignItems: "center",
							padding: "0 0px",
						}}
					>
						<Typography
							sx={{
								fontSize: "8px",
								color: placeholderTextColor,
							}}
						>
							{label}
						</Typography>
					</Box>
				)}
			</Box>
		);
	};

	const DiscountSection = () => {
		const { t } = useTranslation();

		return (
			<Box
				sx={{
					padding: 1,
					width: "100%",
					maxWidth: "400px",
					margin: "0 auto",
					"& *": {
						fontFamily: creator.fontFamilyCap + "!important",
					},
					backgroundColor: creator.themeColor,
					color: creator.themeMode === "Dark" ? "#ffffff" : "#000000",
					borderBottomLeftRadius: "26px",
					borderBottomRightRadius: "26px",
				}}
			>
				<Typography
					variant="body2"
					sx={{
						fontSize: "6px",
						marginBottom: "4px",
					}}
				>
					{t("discountSection.title")}
				</Typography>

				{creator.inputType === "input2" && (
					<Box sx={{ marginTop: 0 }}>
						<CustomTextField
							label={t("discountSection.placeholder")}
							type="outline"
						/>
					</Box>
				)}
				{creator.inputType === "input3" && (
					<Box sx={{ marginTop: 0 }}>
						<CustomTextField
							label={t("discountSection.placeholder")}
							type="filled"
						/>
					</Box>
				)}
				{creator.inputType === "input1" && (
					<Box sx={{ marginTop: 0 }}>
						<CustomTextField
							label={t("discountSection.placeholder")}
							type="standard"
						/>
					</Box>
				)}

				{/* Botón de continuar */}
				<Button
					variant="contained"
					fullWidth
					sx={{
						backgroundColor: creator ? creator.color : "#45A049",
						"&:hover": {
							backgroundColor: creator ? creator.color : "#45A049",
						},
						color: creator && creator.whiteTextButton ? "#ffffff" : "#000000",
						fontSize: "8px",
						borderRadius: creator && creator.buttonRounded ? "24px" : "0px",
						fontWeight: "bold",
						textTransform: "uppercase",
						height: "28px",
						width: "100%",
					}}
				>
					{t("discountSection.continueButton")}
				</Button>
			</Box>
		);
	};

	useEffect(() => {
		const loadContent = () => {
			switch (sector) {
				case "barber":
					return {
						service: t("homeServices.haircut"),
						staff: "John",
						date: "21 August 2024",
						time: "11:00h",
						price: "€20.00",
						image: ServiceBarber4,
					};

				case "salon":
					return {
						service: t("homeServices.facialCleansing"),
						staff: "Ann",
						date: "21 August 2024",
						time: "11:00h",
						price: "€30.00",
						image: Service4,
					};

				case "trainer":
					return {
						service: t("homeServices.personalTraining"),
						staff: "Chris",
						date: "21 August 2024",
						time: "11:00h",
						price: "€50.00",
						image: ServiceTrainer1,
					};

				default:
					return {};
			}
		};

		setContent(loadContent());
	}, [sector, t]);

	function hexToRGBA(hex, opacity) {
		let r = parseInt(hex.slice(1, 3), 16);
		let g = parseInt(hex.slice(3, 5), 16);
		let b = parseInt(hex.slice(5, 7), 16);

		return `rgba(${r}, ${g}, ${b}, ${opacity})`;
	}

	return (
		<Box
			sx={{
				position: "relative",
				display: "flex",
				flexDirection: "column",
				justifyContent: "space-between",
				flex: 1,
				"& *": {
					fontFamily: creator.fontFamilyCap + "!important",
				},
				backgroundColor: creator.themeColor,
				color: creator.themeMode === "Dark" ? "#ffffff" : "#000000",
				borderBottomLeftRadius: "26px",
				borderBottomRightRadius: "26px",
			}}
		>
			<Box>
				{content && appointment === "appointmentConfirmationStyle1" && (
					<Box sx={{ padding: 1.5 }}>
						<Box
							sx={{
								backgroundColor: cardBackgroundColor,
								color: getTextColorBasedOnBackground(
									cardBackgroundColor || "#ffffff"
								),
								padding: 1,
								borderRadius: showBorderRadius ? "4px" : 0,
								display: "flex",
								flexDirection: "column",
								gap: 1,
								border:
									creator.themeMode === "Dark"
										? `1px solid ${hexToRGBA("#ffffff", 0.1)}`
										: `1px solid ${hexToRGBA("#000000", 0.1)}`,
								fontFamily: creator.fontFamilyCap + "!important",
							}}
						>
							<Box sx={{ display: "flex", justifyContent: "space-between" }}>
								<Box sx={{ display: "flex", flexDirection: "column" }}>
									<Typography variant="body1" sx={{ fontSize: "8px" }}>
										{content.service}
									</Typography>
									<Typography variant="body1" sx={{ fontSize: "8px" }}>
										{t("bookingConfirmation.with")} {content.staff}
									</Typography>
								</Box>
								<Typography
									variant="body2"
									sx={{ fontSize: "8px", fontWeight: "bold" }}
								>
									{content.price}
								</Typography>
							</Box>

							<Box sx={{ display: "flex", justifyContent: "space-between" }}>
								<Typography
									variant="body2"
									sx={{ fontWeight: "bold", fontSize: "8px" }}
								>
									{content.date}
								</Typography>
								<Typography
									variant="body2"
									sx={{ fontSize: "8px", fontWeight: "bold" }}
								>
									{content.time}
								</Typography>
							</Box>
						</Box>
					</Box>
				)}
				{content && appointment === "appointmentConfirmationStyle2" && (
					<Box sx={{ padding: 1 }}>
						<Box
							sx={{
								backgroundColor: cardBackgroundColor || "#ffffff",
								color: getTextColorBasedOnBackground(
									cardBackgroundColor || "#ffffff"
								),
								padding: 1,
								borderRadius: "0px",
								boxShadow: showShadow
									? "0px 2px 8px rgba(0, 0, 0, 0.05)"
									: "none",
								display: "flex",
								flexDirection: "row",
								gap: showImage ? 1.5 : 0,
								flex: 1,
							}}
						>
							<Box>
								{showImage && (
									<Avatar
										src={content.image}
										alt={content.service}
										variant="square"
										sx={{
											width: "50px",
											height: "50px",
											aspectRatio: 1,
											objectFit: "cover",
										}}
									/>
								)}
							</Box>
							<Box
								sx={{
									display: "flex",
									flexDirection: "column",
									flex: 1,
								}}
							>
								<Typography variant="body1" sx={{ fontSize: "8px" }}>
									{content.service}
								</Typography>
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 0.5,
										fontSize: "8px",
									}}
								>
									{showAllIcons && (
										<CalendarOutline
											height="7px"
											width="7px"
											color={getTextColorBasedOnBackground(
												cardBackgroundColor || "#ffffff"
											)}
										/>
									)}
									<Typography variant="body2" sx={{ fontSize: "8px" }}>
										{content.date}
									</Typography>
								</Box>
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 0.5,
										fontSize: "8px",
									}}
								>
									{showAllIcons && (
										<TimeOutline
											height="7px"
											width="7px"
											color={getTextColorBasedOnBackground(
												cardBackgroundColor || "#ffffff"
											)}
										/>
									)}
									<Typography variant="body2" sx={{ fontSize: "8px" }}>
										{content.time}
									</Typography>
								</Box>
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 0.5,
										fontSize: "8px",
									}}
								>
									{showAllIcons && (
										<PersonOutline
											height="7px"
											width="7px"
											color={getTextColorBasedOnBackground(
												cardBackgroundColor || "#ffffff"
											)}
										/>
									)}
									<Typography variant="body2" sx={{ fontSize: "8px" }}>
										{t("bookingConfirmation.with")} {content.staff}
									</Typography>
								</Box>
								<Typography
									variant="body2"
									sx={{ fontSize: "8px", fontWeight: "bold", marginTop: 0.4 }}
								>
									{content.price}
								</Typography>
							</Box>
						</Box>
					</Box>
				)}
				{content && appointment === "appointmentConfirmationStyle3" && (
					<Box sx={{ padding: 1 }}>
						<Box
							sx={{
								backgroundColor: cardBackgroundColor || "#ffffff",
								color: getTextColorBasedOnBackground(
									cardBackgroundColor || "#ffffff"
								),
								boxShadow: showShadow
									? "0px 2px 8px rgba(0, 0, 0, 0.05)"
									: "none",
								display: "flex",
								flexDirection: "column",
							}}
						>
							<Box sx={{ position: "relative" }}>
								<Avatar
									src={content.image}
									alt={content.service}
									variant="square"
									sx={{
										width: "100%",
										height: "70px",
										objectFit: "cover",
									}}
								/>
								<Box
									sx={{
										position: "absolute",
										bottom: 0,
										left: 0,
										width: "100%",
										color: "#ffffff",
										padding: "4px 8px",
										display: "flex",
										alignItems: "center",
										justifyContent: "flex-start",
									}}
								>
									<Typography
										variant="body2"
										sx={{
											fontSize: "12px",
											textShadow: "1px 1px 2px rgba(0, 0, 0, 0.1)",
										}}
									>
										{content.service}
									</Typography>
								</Box>
							</Box>

							<Box sx={{ padding: 1 }}>
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 0.5,
										fontSize: "8px",
									}}
								>
									{showAllIcons && (
										<CalendarOutline
											height="7px"
											width="7px"
											color={getTextColorBasedOnBackground(
												cardBackgroundColor || "#ffffff"
											)}
										/>
									)}
									<Typography variant="body2" sx={{ fontSize: "8px" }}>
										{content.date}
									</Typography>
								</Box>
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 0.5,
										fontSize: "8px",
									}}
								>
									{showAllIcons && (
										<TimeOutline
											height="7px"
											width="7px"
											color={getTextColorBasedOnBackground(
												cardBackgroundColor || "#ffffff"
											)}
										/>
									)}
									<Typography variant="body2" sx={{ fontSize: "8px" }}>
										{content.time}
									</Typography>
								</Box>
								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 0.5,
										fontSize: "8px",
									}}
								>
									{showAllIcons && (
										<PersonOutline
											height="7px"
											width="7px"
											color={getTextColorBasedOnBackground(
												cardBackgroundColor || "#ffffff"
											)}
										/>
									)}
									<Typography variant="body2" sx={{ fontSize: "8px" }}>
										{t("bookingConfirmation.with")} {content.staff}
									</Typography>
								</Box>
								<Typography
									variant="body2"
									sx={{ fontWeight: "bold", fontSize: "8px" }}
								>
									{content.price}
								</Typography>
							</Box>
						</Box>
					</Box>
				)}
			</Box>
			<DiscountSection />
		</Box>
	);
};

export default ShowcaseBookingConfirmation;
